import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowDownTrayIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Bar, BarChart, Cell, XAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { DatePicker } from '@/components/ui/date-picker';
import { Tip } from '@/components/ui/tip';
import { CHART } from '@/components/reports/chartTheme';
import { tipFormatter } from '@/components/reports/ChartKit';
import { hoyLocal, toYMD } from '@/utils/fechaLocal';
import { openPdf } from '@/utils/pdfHelpers';

// Resumen del día (v12.5.7): los mismos números del informe diario que salía por
// WhatsApp en PDF, en una página pensada para el celular. Se llega tocando la
// notificación de las 19:00 (sábados 13:00) o desde Finanzas. Los datos salen de
// GET /api/resumen-diario (server/services/daily-report-data.js).

type Monto = { nombre: string; monto: number };

interface Resumen {
  fecha: string;
  ventas: { total: number; cantidad: number; ticket: number };
  lineas: Monto[];
  caja: {
    cobrado: number;
    egresos: number;
    resultado: number;
    efectivoTeorico: number;
    medios: Array<{ metodo: string; monto: number }>;
    /** Los mismos cobros con el nombre de cada medio como está en Configuración */
    mediosConNombre?: Monto[];
    cerrada: boolean;
    cierre: { contado: number; diferencia: number; retiro: number } | null;
  };
  turnos: { total: number; completadosHoy: number; porEstado: Array<{ estado: string; cant: number }> };
  comisiones: { cantidad: number; monto: number };
  posicion: {
    porEntrar: number;
    porSalir: number;
    chequesSemana: { cobrar: number; cobrarCant: number; pagar: number; pagarCant: number };
    chequesVencidos: { cobrar: number; pagar: number };
  };
  deudores: Array<{ nombre: string; saldo: number }>;
  contexto: {
    serie30: Array<{ dia: string; label: string; monto: number }>;
    media30: number;
    deltaVsMedia: number | null;
    acumulado: { mesActual: string; mesAnterior: string; totalActual: number; aEstaAltura: number };
  };
}

const MEDIOS: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tc3: 'Tarjeta en 3 cuotas',
  tc6: 'Tarjeta en 6 cuotas',
  debito: 'Débito',
  qr: 'QR',
  cheque: 'Cheque',
  '(sin medio)': 'Sin medio cargado'
};

const ESTADOS: Record<string, string> = {
  scheduled: 'Agendados',
  pending: 'Pendientes',
  confirmed: 'Confirmados',
  checked_in: 'En recepción',
  in_progress: 'En proceso',
  ready_for_pickup: 'Listos para retirar',
  completed: 'Completados',
  delivered: 'Entregados',
  cancelled: 'Cancelados',
  no_show: 'No vinieron'
};

const pesos = (n: number): string => {
  const v = Math.round(Number(n) || 0);
  return `${v < 0 ? '-' : ''}$ ${Math.abs(v).toLocaleString('es-AR')}`;
};

const esFecha = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(new Date(`${s}T12:00:00`).getTime());

const moverDia = (fecha: string, dias: number): string => {
  const d = new Date(`${fecha}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return toYMD(d);
};

const fechaLarga = (fecha: string): string =>
  new Date(`${fecha}T12:00:00`).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const mayuscula = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ───────────── Piezas ─────────────

const Bloque: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <Card>
    <CardContent className="space-y-3 pt-5">
      <h2 className="text-sm font-semibold leading-none">{titulo}</h2>
      {children}
    </CardContent>
  </Card>
);

/** Renglón "concepto ......... valor". */
const Renglon: React.FC<{ nombre: React.ReactNode; valor: React.ReactNode; fuerte?: boolean }> = ({ nombre, valor, fuerte }) => (
  <div className={`flex items-baseline justify-between gap-3 text-sm ${fuerte ? 'font-semibold' : ''}`}>
    <dt className={fuerte ? '' : 'text-muted-foreground'}>{nombre}</dt>
    <dd className="shrink-0 tabular-nums">{valor}</dd>
  </div>
);

/** Barras horizontales para comparar montos; cada una lleva su nombre, monto y porcentaje. */
const Barras: React.FC<{ items: Monto[]; vacio: string }> = ({ items, vacio }) => {
  const visibles = items.filter((i) => i.monto > 0);
  if (!visibles.length) return <p className="text-sm text-muted-foreground">{vacio}</p>;
  const total = visibles.reduce((s, i) => s + i.monto, 0);
  const max = Math.max(...visibles.map((i) => i.monto));
  return (
    <ul className="space-y-3">
      {visibles.map((i) => (
        <li key={i.nombre}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">{i.nombre}</span>
            <span className="shrink-0 tabular-nums">
              <span className="font-medium">{pesos(i.monto)}</span>
              <span className="ml-2 inline-block w-9 text-right text-xs text-muted-foreground">{Math.round((i.monto / total) * 100)}%</span>
            </span>
          </div>
          <div className="mt-1.5 h-2" aria-hidden="true">
            <div className="h-2 rounded-r-[4px]" style={{ width: `${Math.max(2, (i.monto / max) * 100)}%`, background: CHART.c1 }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

/** Comparación contra una referencia: flecha + porcentaje + contra qué. */
const Variacion: React.FC<{ pct: number | null; contra: string }> = ({ pct, contra }) => {
  if (pct === null || !Number.isFinite(pct)) return null;
  const sube = pct >= 0;
  const Icono = sube ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  return (
    <p className="flex items-start gap-1.5 text-sm">
      <Icono className={`mt-0.5 h-4 w-4 shrink-0 ${sube ? 'text-emerald-600' : 'text-red-600'}`} aria-hidden="true" />
      <span>
        <span className="font-medium">{Math.abs(Math.round(pct))}% {sube ? 'arriba' : 'abajo'}</span>
        <span className="text-muted-foreground"> {contra}</span>
      </span>
    </p>
  );
};

const Aviso: React.FC<{ tipo: 'bien' | 'atencion'; children: React.ReactNode }> = ({ tipo, children }) => {
  const Icono = tipo === 'bien' ? CheckCircleIcon : ExclamationTriangleIcon;
  return (
    <p className="flex items-start gap-2 text-sm">
      <Icono className={`mt-0.5 h-4 w-4 shrink-0 ${tipo === 'bien' ? 'text-emerald-600' : 'text-amber-600'}`} aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
};

// ───────────── Página ─────────────

const ResumenDiario: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const hoy = hoyLocal();
  const pedida = params.get('fecha');
  const fecha = esFecha(pedida) && pedida <= hoy ? pedida : hoy;

  const [datos, setDatos] = useState<Resumen | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [bajando, setBajando] = useState(false);
  const pedido = useRef(0);

  const cargar = useCallback(async () => {
    const turno = ++pedido.current;
    setCargando(true);
    try {
      const { data } = await axios.get('/api/resumen-diario', { params: { fecha } });
      if (turno !== pedido.current) return;            // llegó tarde: ya se pidió otro día
      setDatos(data.datos);
      setError('');
    } catch (e: any) {
      if (turno !== pedido.current) return;
      setError(e?.response?.data?.error || 'No se pudo cargar el resumen.');
    } finally {
      if (turno === pedido.current) setCargando(false);
    }
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  // El día de hoy sigue cambiando: al volver a la app se trae de nuevo
  useEffect(() => {
    if (fecha !== hoy) return undefined;
    const alVolver = () => { if (document.visibilityState === 'visible') cargar(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [fecha, hoy, cargar]);

  const irA = (dia: string) => {
    if (!esFecha(dia) || dia > hoy) return;
    setParams(dia === hoy ? {} : { fecha: dia }, { replace: true });
  };

  const descargar = async () => {
    setBajando(true);
    try {
      const { data } = await axios.get('/api/resumen-diario/pdf', { params: { fecha }, responseType: 'blob' });
      openPdf({ blob: data, filename: `Informe-ROCAM-${fecha}.pdf` });
    } catch {
      toast.error('No se pudo armar el PDF. Probá de nuevo en un momento.');
    } finally {
      setBajando(false);
    }
  };

  // Mientras llega otro día se deja el anterior a la vista, atenuado, sin saltos
  const d = datos;
  const esHoy = fecha === hoy;
  const mes = d?.contexto.acumulado;
  const variacionMes = mes && mes.aEstaAltura > 0 ? (mes.totalActual / mes.aEstaAltura - 1) * 100 : null;

  return (
    <div className="mx-auto max-w-3xl space-y-4 pb-10">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Resumen del día</h1>
        <p className="text-sm text-muted-foreground">
          {mayuscula(fechaLarga(fecha))}
          {esHoy ? ' · hasta ahora' : ''}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Tip label="Día anterior">
          <Button type="button" variant="outline" size="icon" onClick={() => irA(moverDia(fecha, -1))} aria-label="Día anterior">
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
        </Tip>
        <DatePicker value={fecha} onChange={(v) => irA(v || hoy)} className="h-10 w-[150px]" />
        <Tip label="Día siguiente">
          <Button type="button" variant="outline" size="icon" onClick={() => irA(moverDia(fecha, 1))} disabled={esHoy} aria-label="Día siguiente">
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </Tip>
        {!esHoy && (
          <Button type="button" variant="ghost" onClick={() => irA(hoy)}>
            Hoy
          </Button>
        )}
        <Button type="button" variant="secondary" className="ml-auto" onClick={descargar} disabled={bajando || !d}>
          <ArrowDownTrayIcon className="h-4 w-4" />
          {bajando ? 'Armando el PDF...' : 'Descargar PDF'}
        </Button>
      </div>

      {error && (
        <Card>
          <CardContent className="space-y-3 pt-5">
            <Aviso tipo="atencion">{error}</Aviso>
            <Button type="button" variant="outline" onClick={cargar}>
              Reintentar
            </Button>
          </CardContent>
        </Card>
      )}

      {!d && !error && <p className="text-sm text-muted-foreground">Cargando el resumen...</p>}

      {d && (
        <div className={`space-y-4 transition-opacity ${cargando ? 'opacity-60' : ''}`} aria-busy={cargando}>
          <Card>
            <CardContent className="space-y-2 pt-5">
              <p className="text-sm text-muted-foreground">Vendido</p>
              <p className="text-[clamp(2.25rem,11vw,3rem)] font-semibold leading-none">{pesos(d.ventas.total)}</p>
              <p className="text-sm text-muted-foreground">
                {d.ventas.cantidad === 0
                  ? (esHoy ? 'Todavía no hay remitos emitidos.' : 'No hubo remitos emitidos.')
                  : `${d.ventas.cantidad} ${d.ventas.cantidad === 1 ? 'operación' : 'operaciones'} · ticket promedio ${pesos(d.ventas.ticket)}`}
              </p>
              {d.ventas.cantidad > 0 && (
                <Variacion pct={d.contexto.deltaVsMedia} contra={`del promedio de los días con ventas del último mes (${pesos(d.contexto.media30)})`} />
              )}
            </CardContent>
          </Card>

          <Bloque titulo="Plata del día">
            <dl className="space-y-2">
              <Renglon nombre="Cobrado" valor={pesos(d.caja.cobrado)} />
              <Renglon nombre="Gastos" valor={pesos(d.caja.egresos)} />
              <Renglon nombre="Resultado del día" valor={pesos(d.caja.resultado)} fuerte />
            </dl>
            <p className="text-xs text-muted-foreground">Cobrado no incluye lo que quedó en cuenta corriente.</p>
          </Bloque>

          {mes && (
            <Bloque titulo={`${mayuscula(mes.mesActual)} hasta este día`}>
              <dl className="space-y-2">
                <Renglon nombre={`Vendido en ${mes.mesActual}`} valor={pesos(mes.totalActual)} fuerte />
                <Renglon nombre={`${mayuscula(mes.mesAnterior)} a esta altura`} valor={pesos(mes.aEstaAltura)} />
              </dl>
              <Variacion pct={variacionMes} contra={`de ${mes.mesAnterior} a esta altura`} />
            </Bloque>
          )}

          <Bloque titulo="Ventas por línea de negocio">
            <Barras items={d.lineas} vacio="Sin ventas emitidas este día." />
          </Bloque>

          <Bloque titulo="Caja">
            <Barras
              items={d.caja.mediosConNombre || d.caja.medios.map((m) => ({ nombre: MEDIOS[m.metodo] || m.metodo, monto: m.monto }))}
              vacio="Sin cobros este día."
            />
            <dl className="space-y-2 border-t border-border pt-3">
              <Renglon nombre="Efectivo que tendría que haber en caja" valor={pesos(d.caja.efectivoTeorico)} />
              {d.caja.cierre && (
                <>
                  <Renglon nombre="Efectivo contado al cierre" valor={pesos(d.caja.cierre.contado)} />
                  <Renglon nombre="Retiro" valor={pesos(d.caja.cierre.retiro)} />
                </>
              )}
            </dl>
            {!d.caja.cerrada && <Aviso tipo="atencion">La caja de este día está sin cerrar.</Aviso>}
            {d.caja.cierre && Math.round(d.caja.cierre.diferencia) === 0 && <Aviso tipo="bien">Caja cerrada sin diferencia.</Aviso>}
            {d.caja.cierre && Math.round(d.caja.cierre.diferencia) !== 0 && (
              <Aviso tipo="atencion">
                Caja cerrada con {d.caja.cierre.diferencia < 0 ? 'un faltante' : 'un sobrante'} de {pesos(Math.abs(d.caja.cierre.diferencia))}.
              </Aviso>
            )}
          </Bloque>

          <Bloque titulo="Trabajo del día">
            <dl className="space-y-2">
              <Renglon nombre="Turnos del día" valor={d.turnos.total} />
              <Renglon nombre="Trabajos terminados" valor={d.turnos.completadosHoy} />
              {d.turnos.porEstado
                .filter((e) => e.cant > 0)
                .map((e) => (
                  <Renglon key={e.estado} nombre={ESTADOS[e.estado] || e.estado} valor={e.cant} />
                ))}
              <Renglon
                nombre={`Comisiones generadas (${d.comisiones.cantidad} ${d.comisiones.cantidad === 1 ? 'trabajo' : 'trabajos'})`}
                valor={pesos(d.comisiones.monto)}
              />
            </dl>
          </Bloque>

          <Bloque titulo="Lo que viene">
            <dl className="space-y-2">
              <Renglon nombre="Por cobrar (cuentas corrientes y cheques)" valor={pesos(d.posicion.porEntrar)} />
              <Renglon nombre="Por pagar (proveedores y cheques)" valor={pesos(d.posicion.porSalir)} />
              <Renglon
                nombre={`Cheques a cobrar en 7 días (${d.posicion.chequesSemana.cobrarCant})`}
                valor={pesos(d.posicion.chequesSemana.cobrar)}
              />
              <Renglon
                nombre={`Cheques a pagar en 7 días (${d.posicion.chequesSemana.pagarCant})`}
                valor={pesos(d.posicion.chequesSemana.pagar)}
              />
            </dl>
            {d.posicion.chequesVencidos.cobrar > 0 && (
              <Aviso tipo="atencion">Hay cheques a cobrar vencidos por {pesos(d.posicion.chequesVencidos.cobrar)}.</Aviso>
            )}
            {d.posicion.chequesVencidos.pagar > 0 && (
              <Aviso tipo="atencion">Hay cheques propios vencidos sin pagar por {pesos(d.posicion.chequesVencidos.pagar)}.</Aviso>
            )}
            <Link to="/cash-register/checks" className="inline-block text-sm font-medium underline underline-offset-4">
              Ver cheques
            </Link>
          </Bloque>

          <Bloque titulo="Los que más deben">
            {d.deudores.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nadie debe en cuenta corriente.</p>
            ) : (
              <dl className="space-y-2">
                {d.deudores.map((x) => (
                  <Renglon key={x.nombre} nombre={<span className="text-foreground">{x.nombre}</span>} valor={pesos(x.saldo)} />
                ))}
              </dl>
            )}
            <Link to="/current-accounts" className="inline-block text-sm font-medium underline underline-offset-4">
              Ver cuentas corrientes
            </Link>
          </Bloque>

          <Bloque titulo="Ventas de los últimos 30 días">
            <p className="text-xs text-muted-foreground">
              Resaltado, el día del resumen. Promedio de los días con ventas: {pesos(d.contexto.media30)}.
            </p>
            <ChartContainer config={{ monto: { label: 'Vendido', color: CHART.c1 } }} className="h-[170px] w-full">
              <BarChart data={d.contexto.serie30} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} interval={6} tick={{ fontSize: 11 }} />
                <ChartTooltip cursor={false} content={<ChartTooltipContent formatter={tipFormatter({ monto: 'Vendido' }, pesos)} />} />
                <Bar dataKey="monto" radius={[4, 4, 0, 0]} maxBarSize={24}>
                  {d.contexto.serie30.map((s) => (
                    <Cell key={s.dia} fill={s.dia === fecha ? CHART.c1 : 'hsl(var(--muted-foreground) / 0.3)'} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
            <table className="sr-only">
              <caption>Ventas por día de los últimos 30 días</caption>
              <thead>
                <tr>
                  <th scope="col">Día</th>
                  <th scope="col">Vendido</th>
                </tr>
              </thead>
              <tbody>
                {d.contexto.serie30.map((s) => (
                  <tr key={s.dia}>
                    <td>{s.label}</td>
                    <td>{pesos(s.monto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Bloque>
        </div>
      )}
    </div>
  );
};

export default ResumenDiario;
